import React, { useMemo, useState } from 'react'
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme
} from '@mui/material'

import {
  type MatchRateGroupRow,
  type MatchRateOverall,
  type MatchRatePayload,
  type MatchRateStateRow
} from '../../../services/etl/etlService'

// Inline number/percent formatters and tier color helpers — matches the
// pidf-page convention of keeping small UI helpers local to the panel file.

const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming'
}

const rateColor = (rate: number): string => {
  if (rate >= 0.8) return '#1b5e20'
  if (rate >= 0.65) return '#2e7d32'
  if (rate >= 0.5) return '#ed6c02'
  return '#c62828'
}

const rateBg = (rate: number): string => {
  if (rate >= 0.8) return '#e8f5e9'
  if (rate >= 0.65) return '#f1f8e9'
  if (rate >= 0.5) return '#fff4e5'
  return '#ffebee'
}

const fmtNum = (n: number): string => n.toLocaleString('en-US')
const fmtPct = (r: number): string => `${(r * 100).toFixed(2)}%`

type VizStyle = 'bars' | 'table' | 'grid'
type SortKey = 'name' | 'total' | 'match_rate'

interface SortControl {
  key: SortKey
  label: string
}

interface MatchRatePanelProps {
  metrics: MatchRatePayload
}

export const MatchRatePanel: React.FC<MatchRatePanelProps> = ({ metrics }) => {
  const [vizStyle, setVizStyle] = useState<VizStyle>('bars')
  const [stateSort, setStateSort] = useState<SortKey>('match_rate')
  const [groupSort, setGroupSort] = useState<SortKey>('match_rate')

  const { overall, by_state: byState, by_group: byGroup } = metrics

  const stateSortKeys: SortControl[] = [
    { key: 'name', label: 'State' },
    { key: 'total', label: 'Total' },
    { key: 'match_rate', label: 'Rate' }
  ]

  const groupSortKeys: SortControl[] = [
    { key: 'name', label: 'Group' },
    { key: 'total', label: 'Total' },
    { key: 'match_rate', label: 'Rate' }
  ]

  const stateMaxTotal = byState.length > 0 ? Math.max(...byState.map(r => r.total), 1) : 1
  const groupMaxTotal = byGroup.length > 0 ? Math.max(...byGroup.map(r => r.total), 1) : 1

  return (
    <Box sx={{ padding: '12px 24px 32px', background: '#fff' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={vizStyle}
          onChange={(_, v: VizStyle | null) => { if (v !== null) setVizStyle(v) }}
          aria-label="visualization style"
        >
          <ToggleButton value="bars">Bars</ToggleButton>
          <ToggleButton value="table">Table</ToggleButton>
          <ToggleButton value="grid">Grid</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <OverallCard overall={overall} byState={byState} byGroup={byGroup} />

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mt: 3 }}>
        <Section
          title="By State"
          subtitle={`${byState.length} jurisdictions`}
          sort={stateSort}
          setSort={setStateSort}
          sortKeys={stateSortKeys}
        >
          <BreakdownList<MatchRateStateRow>
            rows={byState}
            keyField="state"
            labelOf={(r) => STATE_NAMES[r.state] ?? r.state}
            badgeOf={(r) => r.state}
            sort={stateSort}
            vizStyle={vizStyle}
            maxTotal={stateMaxTotal}
          />
        </Section>

        <Section
          title="By Group"
          subtitle={`${byGroup.length} groups`}
          sort={groupSort}
          setSort={setGroupSort}
          sortKeys={groupSortKeys}
        >
          <BreakdownList<MatchRateGroupRow>
            rows={byGroup}
            keyField="group"
            labelOf={(r) => r.group}
            sort={groupSort}
            vizStyle={vizStyle}
            maxTotal={groupMaxTotal}
          />
        </Section>
      </Box>

      <Legend />
    </Box>
  )
}

interface OverallCardProps {
  overall: MatchRateOverall
  byState: MatchRateStateRow[]
  byGroup: MatchRateGroupRow[]
}

const OverallCard: React.FC<OverallCardProps> = ({ overall, byState, byGroup }) => {
  const unmatched = overall.total - overall.matched
  const topState = byState.length > 0
    ? [...byState].sort((a, b) => b.match_rate - a.match_rate)[0]
    : null
  const bottomState = byState.length > 0
    ? [...byState].sort((a, b) => a.match_rate - b.match_rate)[0]
    : null
  const topGroup = byGroup.length > 0
    ? [...byGroup].sort((a, b) => b.match_rate - a.match_rate)[0]
    : null
  const bottomGroup = byGroup.length > 0
    ? [...byGroup].sort((a, b) => a.match_rate - b.match_rate)[0]
    : null

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: '1.2fr 1fr 1fr',
      gap: 3,
      background: '#fafafa',
      border: '1px solid #e0e0e0',
      borderRadius: 1,
      padding: 3
    }}>
      <Box>
        <SectionEyebrow>Overall Match Rate</SectionEyebrow>
        <Typography sx={{
          fontSize: 56,
          fontWeight: 300,
          color: rateColor(overall.match_rate),
          lineHeight: 1,
          mt: 1
        }}>
          {fmtPct(overall.match_rate)}
        </Typography>
        <Typography component="div" sx={{ mt: 1.5, fontSize: 13, color: '#424242' }}>
          <strong>{fmtNum(overall.matched)}</strong> matched
          <Box component="span" sx={{ color: '#9e9e9e' }}> / </Box>
          <strong>{fmtNum(overall.total)}</strong> total
          <Box component="span" sx={{ color: '#9e9e9e' }}> · </Box>
          <Box component="span">{fmtNum(unmatched)} unmatched</Box>
        </Typography>
        <Box sx={{
          mt: 1.5,
          height: 8,
          background: '#ececec',
          borderRadius: 0.5,
          overflow: 'hidden'
        }}>
          <Box sx={{
            width: `${overall.match_rate * 100}%`,
            background: rateColor(overall.match_rate),
            height: '100%'
          }} />
        </Box>
      </Box>

      <Box sx={{ borderLeft: '1px solid #e0e0e0', pl: 3 }}>
        <SectionEyebrow sx={{ mb: 1.5 }}>Highlights — States</SectionEyebrow>
        {(topState != null) && (
          <HighlightRow
            label="Highest"
            badge={topState.state}
            sub={STATE_NAMES[topState.state] ?? topState.state}
            rate={topState.match_rate}
          />
        )}
        {(bottomState != null) && (
          <HighlightRow
            label="Lowest"
            badge={bottomState.state}
            sub={STATE_NAMES[bottomState.state] ?? bottomState.state}
            rate={bottomState.match_rate}
          />
        )}
      </Box>

      <Box sx={{ borderLeft: '1px solid #e0e0e0', pl: 3 }}>
        <SectionEyebrow sx={{ mb: 1.5 }}>Highlights — Groups</SectionEyebrow>
        {(topGroup != null) && (
          <HighlightRow label="Highest" sub={topGroup.group} rate={topGroup.match_rate} />
        )}
        {(bottomGroup != null) && (
          <HighlightRow label="Lowest" sub={bottomGroup.group} rate={bottomGroup.match_rate} />
        )}
      </Box>
    </Box>
  )
}

interface SectionEyebrowProps {
  children: React.ReactNode
  sx?: object
}

const SectionEyebrow: React.FC<SectionEyebrowProps> = ({ children, sx = {} }) => (
  <Typography sx={{
    fontSize: 12,
    color: '#616161',
    textTransform: 'uppercase',
    letterSpacing: '.08em',
    fontWeight: 600,
    ...sx
  }}>{children}</Typography>
)

interface HighlightRowProps {
  label: string
  badge?: string
  sub: string
  rate: number
}

const HighlightRow: React.FC<HighlightRowProps> = ({ label, badge, sub, rate }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25 }}>
    <Box sx={{
      fontSize: 11,
      color: '#9e9e9e',
      width: 50,
      textTransform: 'uppercase',
      letterSpacing: '.06em'
    }}>{label}</Box>
    {(badge !== undefined && badge !== '') && (
      <Box sx={{
        fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace',
        fontSize: 12,
        fontWeight: 600,
        background: '#fff',
        border: '1px solid #e0e0e0',
        px: 0.75,
        py: 0.25,
        borderRadius: 0.5,
        minWidth: 28,
        textAlign: 'center'
      }}>{badge}</Box>
    )}
    <Box sx={{
      fontSize: 13,
      color: '#212121',
      flex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }}>{sub}</Box>
    <Box sx={{ fontSize: 13, fontWeight: 600, color: rateColor(rate) }}>{fmtPct(rate)}</Box>
  </Box>
)

interface SectionProps {
  title: string
  subtitle: string
  sort: SortKey
  setSort: (k: SortKey) => void
  sortKeys: SortControl[]
  children: React.ReactNode
}

const Section: React.FC<SectionProps> = ({ title, subtitle, sort, setSort, sortKeys, children }) => {
  const theme = useTheme()
  return (
    <Box sx={{
      border: '1px solid #e0e0e0',
      borderRadius: 1,
      background: '#fff',
      overflow: 'hidden'
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: '14px 16px',
        borderBottom: '1px solid #e0e0e0',
        background: '#fafafa'
      }}>
        <Box>
          <Typography component="span" sx={{ fontSize: 15, fontWeight: 600, color: '#212121' }}>{title}</Typography>
          <Typography component="span" sx={{ ml: 1.25, fontSize: 12, color: '#9e9e9e' }}>{subtitle}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography component="span" sx={{
            fontSize: 11,
            color: '#9e9e9e',
            textTransform: 'uppercase',
            letterSpacing: '.06em'
          }}>Sort</Typography>
          <Box sx={{
            display: 'inline-flex',
            border: '1px solid #cfcfcf',
            borderRadius: 0.5,
            overflow: 'hidden'
          }}>
            {sortKeys.map((sk) => (
              <button
                key={sk.key}
                onClick={() => { setSort(sk.key) }}
                style={{
                  background: sort === sk.key ? theme.palette.primary.main : '#fff',
                  color: sort === sk.key ? '#fff' : '#424242',
                  border: 'none',
                  padding: '4px 9px',
                  fontSize: 11,
                  fontWeight: 500,
                  cursor: 'pointer',
                  letterSpacing: '.04em',
                  fontFamily: 'inherit'
                }}
              >{sk.label}</button>
            ))}
          </Box>
        </Box>
      </Box>
      <Box>{children}</Box>
    </Box>
  )
}

type BreakdownRow = MatchRateStateRow | MatchRateGroupRow

interface BreakdownListProps<T extends BreakdownRow> {
  rows: T[]
  keyField: 'state' | 'group'
  labelOf: (r: T) => string
  badgeOf?: (r: T) => string
  sort: SortKey
  vizStyle: VizStyle
  maxTotal: number
}

function BreakdownList<T extends BreakdownRow> ({
  rows, keyField, labelOf, badgeOf, sort, vizStyle, maxTotal
}: BreakdownListProps<T>): React.ReactElement {
  const sorted = useMemo(() => {
    const copy = [...rows]
    if (sort === 'match_rate') {
      copy.sort((a, b) => b.match_rate - a.match_rate)
    } else if (sort === 'total') {
      copy.sort((a, b) => b.total - a.total)
    } else {
      copy.sort((a, b) => {
        const av = (a as unknown as Record<string, unknown>)[keyField]
        const bv = (b as unknown as Record<string, unknown>)[keyField]
        return String(av ?? '').localeCompare(String(bv ?? ''))
      })
    }
    return copy
  }, [rows, sort, keyField])

  if (vizStyle === 'table') {
    return <TableView rows={sorted} labelOf={labelOf} badgeOf={badgeOf} keyField={keyField} />
  }
  if (vizStyle === 'grid') {
    return <GridView rows={sorted} labelOf={labelOf} badgeOf={badgeOf} keyField={keyField} />
  }
  return <BarsView rows={sorted} labelOf={labelOf} badgeOf={badgeOf} keyField={keyField} maxTotal={maxTotal} />
}

interface ViewProps<T extends BreakdownRow> {
  rows: T[]
  keyField: 'state' | 'group'
  labelOf: (r: T) => string
  badgeOf?: (r: T) => string
}

const rowKey = <T extends BreakdownRow>(r: T, keyField: 'state' | 'group'): string => {
  return String((r as unknown as Record<string, unknown>)[keyField] ?? '')
}

function BarsView<T extends BreakdownRow> ({
  rows, keyField, labelOf, badgeOf, maxTotal
}: ViewProps<T> & { maxTotal: number }): React.ReactElement {
  return (
    <Box sx={{ maxHeight: 520, overflowY: 'auto' }}>
      {rows.map((r) => {
        const k = rowKey(r, keyField)
        const totalPct = (r.total / maxTotal) * 100
        const matchedPct = r.total > 0 ? (r.matched / r.total) * 100 : 0
        return (
          <Box key={k} sx={{
            display: 'grid',
            gridTemplateColumns: '180px 1fr 90px 70px',
            alignItems: 'center',
            gap: 1.5,
            p: '10px 16px',
            borderBottom: '1px solid #f0f0f0',
            fontSize: 13
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              {(badgeOf !== undefined) && (
                <Box sx={{
                  fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace',
                  fontSize: 11,
                  fontWeight: 600,
                  background: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  padding: '1px 5px',
                  borderRadius: 0.5,
                  color: '#616161'
                }}>{badgeOf(r)}</Box>
              )}
              <Box
                title={labelOf(r)}
                sx={{
                  color: '#212121',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >{labelOf(r)}</Box>
            </Box>
            <Box sx={{
              position: 'relative',
              height: 18,
              background: '#f5f5f5',
              borderRadius: 0.5,
              overflow: 'hidden',
              width: `${totalPct}%`,
              minWidth: 20
            }}>
              <Box sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: `${matchedPct}%`,
                background: rateColor(r.match_rate)
              }} />
            </Box>
            <Box sx={{
              fontVariantNumeric: 'tabular-nums',
              color: '#616161',
              fontSize: 12,
              textAlign: 'right'
            }}>
              {fmtNum(r.matched)} / {fmtNum(r.total)}
            </Box>
            <Box sx={{
              fontVariantNumeric: 'tabular-nums',
              fontWeight: 600,
              fontSize: 13,
              color: rateColor(r.match_rate),
              textAlign: 'right'
            }}>
              {fmtPct(r.match_rate)}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

const TH_STYLE: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: '#616161',
  textTransform: 'uppercase',
  letterSpacing: '.06em',
  borderBottom: '1px solid #e0e0e0'
}
const TD_STYLE: React.CSSProperties = { padding: '8px 14px', color: '#212121' }

function TableView<T extends BreakdownRow> ({
  rows, keyField, labelOf, badgeOf
}: ViewProps<T>): React.ReactElement {
  return (
    <Box sx={{ maxHeight: 520, overflowY: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead style={{ position: 'sticky', top: 0, background: '#fafafa', zIndex: 1 }}>
          <tr>
            {(badgeOf !== undefined) && <th style={TH_STYLE} />}
            <th style={TH_STYLE}>Name</th>
            <th style={{ ...TH_STYLE, textAlign: 'right' }}>Matched</th>
            <th style={{ ...TH_STYLE, textAlign: 'right' }}>Total</th>
            <th style={{ ...TH_STYLE, textAlign: 'right' }}>Rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const k = rowKey(r, keyField)
            return (
              <tr key={k} style={{ borderTop: '1px solid #f0f0f0' }}>
                {(badgeOf !== undefined) && (
                  <td style={{
                    ...TD_STYLE,
                    fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace',
                    fontWeight: 600,
                    color: '#616161'
                  }}>{badgeOf(r)}</td>
                )}
                <td style={TD_STYLE}>{labelOf(r)}</td>
                <td style={{
                  ...TD_STYLE,
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                  color: '#616161'
                }}>{fmtNum(r.matched)}</td>
                <td style={{
                  ...TD_STYLE,
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                  color: '#616161'
                }}>{fmtNum(r.total)}</td>
                <td style={{
                  ...TD_STYLE,
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 600,
                  color: rateColor(r.match_rate)
                }}>{fmtPct(r.match_rate)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </Box>
  )
}

function GridView<T extends BreakdownRow> ({
  rows, keyField, labelOf, badgeOf
}: ViewProps<T>): React.ReactElement {
  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
      gap: 1,
      p: 1.75,
      maxHeight: 520,
      overflowY: 'auto'
    }}>
      {rows.map((r) => {
        const k = rowKey(r, keyField)
        const accent = rateColor(r.match_rate)
        return (
          <Box
            key={k}
            title={`${labelOf(r)} — ${fmtNum(r.matched)}/${fmtNum(r.total)}`}
            sx={{
              background: rateBg(r.match_rate),
              border: `1px solid ${accent}33`,
              borderLeft: `3px solid ${accent}`,
              borderRadius: 0.5,
              padding: '8px 10px'
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 0.75
            }}>
              <Box sx={{
                fontFamily: (badgeOf !== undefined)
                  ? 'ui-monospace,SFMono-Regular,Menlo,monospace'
                  : 'inherit',
                fontSize: (badgeOf !== undefined) ? 12 : 11,
                fontWeight: 600,
                color: '#212121',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {(badgeOf !== undefined) ? badgeOf(r) : labelOf(r)}
              </Box>
              <Box sx={{
                fontSize: 12,
                fontWeight: 700,
                color: accent,
                fontVariantNumeric: 'tabular-nums'
              }}>
                {(r.match_rate * 100).toFixed(0)}%
              </Box>
            </Box>
            <Box sx={{
              fontSize: 10.5,
              color: '#757575',
              mt: 0.25,
              fontVariantNumeric: 'tabular-nums'
            }}>
              {fmtNum(r.matched)}/{fmtNum(r.total)}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

const Legend: React.FC = () => {
  const tiers = [
    { label: '≥ 80%', color: rateColor(0.85) },
    { label: '65–80%', color: rateColor(0.7) },
    { label: '50–65%', color: rateColor(0.55) },
    { label: '< 50%', color: rateColor(0.4) }
  ]
  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      mt: 2,
      fontSize: 12,
      color: '#616161'
    }}>
      <Box component="span" sx={{
        textTransform: 'uppercase',
        letterSpacing: '.06em',
        fontSize: 11
      }}>Rate tiers</Box>
      {tiers.map((t) => (
        <Box
          key={t.label}
          component="span"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
        >
          <Box component="span" sx={{
            width: 12,
            height: 12,
            background: t.color,
            borderRadius: 0.25
          }} />
          {t.label}
        </Box>
      ))}
    </Box>
  )
}
